import { Button, ButtonLink } from './Input2';
import { Alerts } from './StrokeIcons';

export type EmailCredentialsBannerFailure = {
  id: string;
  lastSyncError: string | null;
};

export type EmailCredentialsBannerPureProps = {
  /** Credentials that currently have a sync error. Banner hides when empty. */
  failedCredentials: EmailCredentialsBannerFailure[];
  /** True while the retry mutation is in flight. */
  isRetrying: boolean;
  /** Retry the first failed credential. */
  onRetry: () => void;
  /** Where "Go to Settings" links. */
  settingsHref?: string;
};

/**
 * Presentational banner shown when email credentials have authentication errors.
 * Pure: no data fetching — the container supplies the failures + retry handler.
 */
export const EmailCredentialsBannerPure = ({
  failedCredentials,
  isRetrying,
  onRetry,
  settingsHref = '/teams/active/user/me/automation',
}: EmailCredentialsBannerPureProps) => {
  const firstFailed = failedCredentials[0];
  if (!firstFailed) {
    return null;
  }

  return (
    <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-white">
      <div className="flex items-center gap-2">
        <Alerts.AlertTriangle className="h-5 w-5" />
        <span className="font-medium">Email connection issue{failedCredentials.length > 1 ? 's' : ''}:</span>
        <span className="text-amber-100">{firstFailed.lastSyncError}</span>
        {failedCredentials.length > 1 && (
          <span className="text-amber-100">({failedCredentials.length} accounts affected)</span>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={onRetry}
          disabled={isRetrying}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          {isRetrying ? 'Retrying...' : 'Retry Connection'}
        </Button>
        <ButtonLink href={settingsHref} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
          Go to Settings
        </ButtonLink>
      </div>
    </div>
  );
};
